import { useCallback, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Toggle, TextInput } from '@components/ui';
import Select from '@workpay/ds-shared/components/ui/Select';
import Modal from '@components/ui/Modal/Modal';
import { createEmploymentType, updateEmploymentType } from '@/services/employmentTypeService';
import { useTranslation } from '@/hooks/useTranslation';
import { useNotificationStore } from '@stores/notificationStore';
import { WorkpayApiError } from '@lib/apiEnvelope';
import type { EmploymentTypeDto } from '@/types/employmentType';
import styles from './EmploymentTypeModals.module.scss';

// ============================================
// TYPES
// ============================================

interface CreateEditEmploymentTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: EmploymentTypeDto | null;
}

interface EmploymentTypeFormValues {
  name: string;
  taxType: string;
  payroll: boolean;
  statutoryBenefits: boolean;
  invoiceWorkflow: string;
  invoiceWorkflowOverridable: boolean;
}

// ============================================
// OPTIONS
// ============================================

// Option values (labels translated inside component)
const TAX_TYPE_VALUES = ['PAYE', 'NONE', 'WITHHOLDING_TAX'] as const;
const INVOICE_WORKFLOW_VALUES = ['NOT_APPLICABLE', 'REQUIRED'] as const;

const DEFAULT_VALUES: EmploymentTypeFormValues = {
  name: '',
  taxType: '',
  payroll: false,
  statutoryBenefits: false,
  invoiceWorkflow: '',
  invoiceWorkflowOverridable: false,
};

// ============================================
// COMPONENT
// ============================================

function CreateEditEmploymentTypeModal({
  isOpen,
  onClose,
  editData,
}: CreateEditEmploymentTypeModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const isEditMode = Boolean(editData);

  const TAX_TYPE_OPTIONS = useMemo(() => [
    { label: t('K_OPTION_PAYE', 'PAYE'), value: TAX_TYPE_VALUES[0] },
    { label: t('K_OPTION_NONE', 'None'), value: TAX_TYPE_VALUES[1] },
    { label: t('K_OPTION_WITHHOLDING_TAX', 'Withholding Tax'), value: TAX_TYPE_VALUES[2] },
  ], [t]);

  const INVOICE_WORKFLOW_OPTIONS = useMemo(() => [
    { label: t('K_OPTION_NOT_APPLICABLE', 'Not Applicable'), value: INVOICE_WORKFLOW_VALUES[0] },
    { label: t('K_OPTION_REQUIRED', 'Required'), value: INVOICE_WORKFLOW_VALUES[1] },
  ], [t]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<EmploymentTypeFormValues>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });

  useEffect(() => {
    if (!isOpen) return;
    if (editData) {
      reset({
        name: editData.name,
        taxType: editData.taxType,
        payroll: editData.payroll,
        statutoryBenefits: editData.statutoryBenefits,
        invoiceWorkflow: editData.invoiceWorkflow,
        invoiceWorkflowOverridable: editData.invoiceWorkflowOverridable,
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [isOpen, editData, reset]);

  const createMutation = useMutation({
    mutationFn: createEmploymentType,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employmentTypes'] });
      reset();
      onClose();
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const updateMutation = useMutation({
    mutationFn: useCallback(
      (data: EmploymentTypeFormValues) => {
        const payload = {
          name: data.name,
          taxType: data.taxType as EmploymentTypeDto['taxType'],
          payroll: data.payroll,
          statutoryBenefits: data.statutoryBenefits,
          invoiceWorkflow: data.invoiceWorkflow as EmploymentTypeDto['invoiceWorkflow'],
          invoiceWorkflowOverridable: data.invoiceWorkflowOverridable,
        };
        return updateEmploymentType(editData!.id, payload);
      },
      [editData],
    ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employmentTypes'] });
      reset();
      onClose();
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const mutation = isEditMode ? updateMutation : createMutation;

  const onSubmit = useCallback(
    (data: EmploymentTypeFormValues) => {
      if (isEditMode) {
        updateMutation.mutate(data);
      } else {
        createMutation.mutate({
          name: data.name,
          taxType: data.taxType as EmploymentTypeDto['taxType'],
          payroll: data.payroll,
          statutoryBenefits: data.statutoryBenefits,
          invoiceWorkflow: data.invoiceWorkflow as EmploymentTypeDto['invoiceWorkflow'],
          invoiceWorkflowOverridable: data.invoiceWorkflowOverridable,
        });
      }
    },
    [isEditMode, createMutation, updateMutation],
  );

  const handleClose = useCallback(() => {
    if (!mutation.isPending) {
      reset();
      onClose();
    }
  }, [mutation.isPending, reset, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? t('K_MODAL_EDIT_EMPLOYMENT_TYPE', 'Edit Employment Type') : t('K_MODAL_CREATE_EMPLOYMENT_TYPE', 'Create Employment Type')}
      size="md"
      footer={
        <div className={styles.formFooter}>
          <Button variant="outline" size="sm" onClick={handleClose} disabled={mutation.isPending}>
            {t('K_CANCEL', 'Cancel')}
          </Button>
          <Button
            variant="filled"
            size="sm"
            type="submit"
            form="employment-type-form"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? t('K_BUTTON_SAVING', 'Saving...') : isEditMode ? t('K_UPDATE', 'Update') : t('K_SAVE', 'Save')}
          </Button>
        </div>
      }
    >
      <form id="employment-type-form" onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <TextInput
          label={t('K_LABEL_NAME', 'Name')}
          isRequired
          fullWidth
          placeholder={t('K_PLACEHOLDER_FULL_TIME', 'e.g. Full-time')}
          error={errors.name?.message}
          {...register('name', {
            required: t('K_VALIDATION_NAME_REQUIRED', 'Name is required'),
            maxLength: { value: 255, message: t('K_VALIDATION_MAX_255', 'Max 255 characters') },
          })}
        />

        <div className={styles.row}>
          <Controller
            name="taxType"
            control={control}
            rules={{ required: t('K_VALIDATION_TAX_TYPE_REQUIRED', 'Tax type is required') }}
            render={({ field }) => (
              <Select
                label={t('K_LABEL_TAX_TYPE', 'Tax Type')}
                isRequired
                placeholder={t('K_PLACEHOLDER_TAX_TYPE', 'Select tax type')}
                options={TAX_TYPE_OPTIONS}
                value={field.value}
                onChange={field.onChange}
                error={errors.taxType?.message}
              />
            )}
          />
          <Controller
            name="invoiceWorkflow"
            control={control}
            rules={{ required: t('K_VALIDATION_INVOICE_WORKFLOW_REQUIRED', 'Invoice workflow is required') }}
            render={({ field }) => (
              <Select
                label={t('K_LABEL_INVOICE_WORKFLOW', 'Invoice Workflow')}
                isRequired
                placeholder={t('K_PLACEHOLDER_WORKFLOW', 'Select workflow')}
                options={INVOICE_WORKFLOW_OPTIONS}
                value={field.value}
                onChange={field.onChange}
                error={errors.invoiceWorkflow?.message}
              />
            )}
          />
        </div>

        <div className={styles.toggleRow}>
          <Controller
            name="payroll"
            control={control}
            render={({ field }) => (
              <Toggle
                label={t('K_LABEL_PAYROLL', 'Payroll')}
                isChecked={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="statutoryBenefits"
            control={control}
            render={({ field }) => (
              <Toggle
                label={t('K_LABEL_STATUTORY_BENEFITS', 'Statutory Benefits')}
                isChecked={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="invoiceWorkflowOverridable"
            control={control}
            render={({ field }) => (
              <Toggle
                label={t('K_LABEL_INVOICE_WORKFLOW_OVERRIDABLE', 'Invoice Workflow Overridable')}
                isChecked={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </form>
    </Modal>
  );
}

export default CreateEditEmploymentTypeModal;
